import { create } from 'zustand';
import { ProjectData } from '../dto/EntityRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';


interface ProjectState {
  project: ProjectData | null;
  /** 현재 캐시가 가리키는 projectId (URL과 String 비교용) */
  projectId: string | null;
  projectOwnerAui: string | null;
  projectScope: ResourceScope | null;
  status: ResourceStatus;
  hasChanged: boolean;
  beginProjectFetch: (ownerAui: string, scope: ResourceScope, projectId: string) => void;
  setProject: (
    project: ProjectData,
    ownerAui: string,
    scope: ResourceScope,
    projectId: string,
  ) => void;
  setProjectError: () => void;
  setProjectHeaderAfterUpdate: (project: ProjectData) => void;
  setProjectCoverHeightAfterUpdate: (project: ProjectData) => void;
  setProjectTypeAfterUpdate: (project: ProjectData) => void;
  setProjectYearAfterUpdate: (project: ProjectData) => void;
  updateTitle: (title: string) => void;
  updateImage: (originUrl: string, width: number, height: number) => void;
  updateFocus: (focusX: number, focusY: number) => void;
  clearProject: () => void;
}

const hasPendingProjectChange = (project: ProjectData | null): boolean =>
  Boolean(project?.titleChanged || project?.imageChanged || project?.focusChanged);

const isSameProject = (prev: ProjectData | null, next: ProjectData): boolean => {
  if (!prev) return false;
  return (
    prev.id === next.id &&
    prev.title === next.title &&
    prev.isPublic === next.isPublic &&
    prev.coverHeight === next.coverHeight &&
    prev.focusX === next.focusX &&
    prev.focusY === next.focusY &&
    prev.projectType === next.projectType &&
    prev.projectYear === next.projectYear &&
    prev.uploadFile.id === next.uploadFile.id &&
    prev.uploadFile.originUrl === next.uploadFile.originUrl &&
    prev.uploadFile.width === next.uploadFile.width &&
    prev.uploadFile.height === next.uploadFile.height &&
    prev.uploadFile.allowDownload === next.uploadFile.allowDownload
  );
};

export const useProjectStore = create<ProjectState>((set) => ({
  project: null,
  projectId: null,
  projectOwnerAui: null,
  projectScope: null,
  status: 'idle',
  hasChanged: false,
  beginProjectFetch: (ownerAui, scope, projectId) => set((state) => {
    const normalizedId = String(projectId);
    // 같은 owner+scope+projectId 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.projectOwnerAui === ownerAui &&
      state.projectScope === scope &&
      state.projectId === normalizedId &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setProject: (project, ownerAui, scope, projectId) =>
    set((state) => {
      const normalizedId = String(projectId);
      if (
        state.status === 'success' &&
        state.projectOwnerAui === ownerAui &&
        state.projectScope === scope &&
        state.projectId === normalizedId &&
        isSameProject(state.project, project) &&
        !state.project?.titleChanged &&
        !state.project?.imageChanged &&
        !state.project?.focusChanged
      ) {
        return state;
      }
      return {
        project: { ...project, titleChanged: false, imageChanged: false, focusChanged: false },
        projectId: normalizedId,
        projectOwnerAui: ownerAui,
        projectScope: scope,
        status: 'success',
        hasChanged: false,
      };
    }),
  setProjectError: () => set({ status: 'error' }),
  setProjectHeaderAfterUpdate: (updatedProject) => set((state) => {
    const project = state.project ? {
      ...state.project,
      title: updatedProject.title,
      uploadFile: updatedProject.uploadFile,
      focusX: updatedProject.focusX,
      focusY: updatedProject.focusY,
      titleChanged: false,
      imageChanged: false,
      focusChanged: false,
    } : null;
    return { project, hasChanged: hasPendingProjectChange(project) };
  }),
  setProjectCoverHeightAfterUpdate: (updatedProject) => set((state) => {
    const project = state.project ? {
      ...state.project,
      coverHeight: updatedProject.coverHeight,
    } : null;
    return { project, hasChanged: hasPendingProjectChange(project) };
  }),
  setProjectTypeAfterUpdate: (updatedProject) => set((state) => {
    const project = state.project ? {
      ...state.project,
      projectType: updatedProject.projectType,
    } : null;
    return { project, hasChanged: hasPendingProjectChange(project) };
  }),
  setProjectYearAfterUpdate: (updatedProject) => set((state) => {
    const project = state.project ? {
      ...state.project,
      projectYear: updatedProject.projectYear,
    } : null;
    return { project, hasChanged: hasPendingProjectChange(project) };
  }),
  updateTitle: (title: string) =>
    set(({ project }) => ({
      project: project ? {
        ...project,
        title,
        titleChanged: true
      } : null,
      hasChanged: true
    })),
  updateImage: (originUrl: string, width: number, height: number) =>
    set(({ project }) => ({
      project: project ? {
        ...project,
        uploadFile: {
          ...project.uploadFile,
          originUrl,
          width,
          height,
        },
        focusX: 0.5,
        focusY: 0.5,
        imageChanged: true,
        focusChanged: false,
      } : null,
      hasChanged: true
    })),
  updateFocus: (focusX, focusY) =>
    set(({ project }) => ({
      project: project ? {
        ...project,
        focusX,
        focusY,
        focusChanged: true,
      } : null,
      hasChanged: true,
    })),
  clearProject: () => set((state) => {
    if (
      state.project === null &&
      state.projectId === null &&
      state.projectOwnerAui === null &&
      state.projectScope === null &&
      state.status === 'idle' &&
      state.hasChanged === false
    ) {
      return state;
    }
    return {
      project: null,
      projectId: null,
      projectOwnerAui: null,
      projectScope: null,
      status: 'idle',
      hasChanged: false,
    };
  }),
}));
